import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDepartment } from '../../context/DepartmentContext';
import styles from './Nav.module.scss';
import Button from '../Button/Button';
import Avatar from '../Avatar/Avatar';
import Select from '../Select/Select';
import { getAvatarSrc } from '../../utils/avatarUtils';

function Nav() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { departments, selectedDepartmentId, setSelectedDepartmentId, loading } = useDepartment();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      
      // Close mobile menu when clicking outside
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && 
          !event.target.closest(`.${styles.nav__menuButton}`)) {
        setMobileMenuOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Transform departments for Select component
  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));

  // Only show department selector on main pages, not on admin page
  const showDepartmentSelector = location.pathname !== '/admin' && !location.pathname.startsWith('/admin/');

  return (
    <nav className={styles.nav}>
      <div className={styles.nav__left}>
      <Link to="/" className={styles.nav__title}>
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 32 32">
        <rect width="32" height="32" fill="#040E12" rx="4"/>
        <path fill="#fff" d="M22.9173 11.8163c-1.1503 0-2.0828-.9558-2.0828-2.13476 0-1.17898.9325-2.13473 2.0828-2.13473 1.1502 0 2.0827.95575 2.0827 2.13473 0 1.17896-.9325 2.13476-2.0827 2.13476Zm-6.6408-3.54683c-1.1503 0-2.0827-.95576-2.0827-2.13474C14.1938 4.95575 15.1262 4 16.2765 4c1.1502 0 2.0827.95575 2.0827 2.13473 0 1.17898-.9325 2.13474-2.0827 2.13474ZM15.5392 28c-1.1503 0-2.0827-.9558-2.0827-2.1347 0-1.179.9324-2.1348 2.0827-2.1348s2.0827.9558 2.0827 2.1348c0 1.1789-.9324 2.1347-2.0827 2.1347ZM9.45137 12.6457c-1.15026 0-2.08273-.9558-2.08273-2.1348 0-1.17894.93247-2.1347 2.08273-2.1347 1.15023 0 2.08273.95576 2.08273 2.1347 0 1.179-.9325 2.1348-2.08273 2.1348Zm6.45643 5.0621c-1.1502 0-2.0827-.9558-2.0827-2.1347 0-1.179.9325-2.1348 2.0827-2.1348 1.1503 0 2.0828.9558 2.0828 2.1348 0 1.1789-.9325 2.1347-2.0828 2.1347Zm6.0879 5.489c-1.1503 0-2.0828-.9557-2.0828-2.1347s.9325-2.1347 2.0828-2.1347c1.1502 0 2.0827.9557 2.0827 2.1347s-.9325 2.1347-2.0827 2.1347ZM9.08273 24.2922C7.93247 24.2922 7 23.3365 7 22.1575s.93247-2.1348 2.08273-2.1348c1.15027 0 2.08277.9558 2.08277 2.1348 0 1.179-.9325 2.1347-2.08277 2.1347Z"/>
      </svg>
      Sk<span>i</span>ma
      </Link>

      {/* Department selector - only shown on main pages */}
      {showDepartmentSelector && !loading && departments.length > 0 && (
        <div className={styles.nav__departmentSelector}>
          <Select
            options={departmentOptions}
            value={selectedDepartmentId}
            onChange={setSelectedDepartmentId}
            placeholder="Select a department"
            id="nav-department-selector"
            showSearch={false}
          />
        </div>
      )}
      </div>

      
      {/* Mobile menu button - only visible when menu is closed */}
      <button 
        className={`${styles.nav__menuButton} ${mobileMenuOpen ? styles.nav__menuButton_hidden : ''}`}
        onClick={() => setMobileMenuOpen(true)}
        aria-label="Open navigation menu"
      >
        <div className={styles.nav__hamburger}>
          <span className={styles.nav__hamburgerLine}></span>
          <span className={styles.nav__hamburgerLine}></span>
          <span className={styles.nav__hamburgerLine}></span>
        </div>
      </button>
      
      {/* Desktop navigation */}
      <div className={styles.nav__links}>
        <Link
          to="/"
          className={`${styles.nav__link} ${location.pathname === '/' ? styles.active : ''}`}
        >
          Matrix
        </Link>
        <Link
          to="/insights"
          className={`${styles.nav__link} ${location.pathname === '/insights' ? styles.active : ''}`}
        >
          Insights
        </Link>
        <Link
          to="/mentoring"
          className={`${styles.nav__link} ${location.pathname === '/mentoring' ? styles.active : ''}`}
        >
          Mentoring
        </Link>
        {user?.role === 'admin' && (
          <Link
            to="/admin"
            className={`${styles.nav__link} ${location.pathname === '/admin' ? styles.active : ''}`}
          >
            Admin
          </Link>
        )}
        {user && (
          <div className={styles.nav__userDropdown} ref={dropdownRef}>
            <div 
              className={styles.nav__user} 
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <Avatar
                src={getAvatarSrc(user, user)}
                name={user.name}
                size="medium"
                className={styles.nav__avatar}
              />
              <span>{user.name}</span>
              <span className={styles.nav__dropdownIcon}>▼</span>
            </div>
            
            {dropdownOpen && (
              <div className={styles.nav__dropdown}>
                <div className={styles.nav__userRole}>
                  Role: <span className={styles.nav__roleValue}>{user.role}</span>
                </div>
                <Button 
                  type="secondary" 
                  onClick={logout}
                  className={styles.nav__dropdownButton}
                >
                  Logout
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Mobile off-canvas menu */}
      <div 
        className={`${styles.nav__mobileMenu} ${mobileMenuOpen ? styles.nav__mobileMenu_open : ''}`}
        ref={mobileMenuRef}
      >
        <div className={styles.nav__mobileMenuContent}>
          <div className={styles.nav__mobileMenuHeader}>
            <button 
              className={styles.nav__closeButton}
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              &times;
            </button>
          </div>
          
          <div className={styles.nav__mobileLinks}>
            <Link
              to="/"
              className={`${styles.nav__mobileLink} ${location.pathname === '/' ? styles.active : ''}`}
            >
              Matrix
            </Link>
            <Link
              to="/insights"
              className={`${styles.nav__mobileLink} ${location.pathname === '/insights' ? styles.active : ''}`}
            >
              Insights
            </Link>
            <Link
              to="/mentoring"
              className={`${styles.nav__mobileLink} ${location.pathname === '/mentoring' ? styles.active : ''}`}
            >
              Mentoring
            </Link>
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className={`${styles.nav__mobileLink} ${location.pathname === '/admin' ? styles.active : ''}`}
              >
                Admin
              </Link>
            )}
          </div>
          
          {user && (
            <div className={styles.nav__mobileUser}>
              <div className={styles.nav__mobileUserInfo}>
                <Avatar
                  src={getAvatarSrc(user, user)}
                  name={user.name}
                  size="medium"
                  className={styles.nav__avatar}
                />
                <div className={styles.nav__mobileUserDetails}>
                  <span>{user.name}</span>
                  <span className={styles.nav__mobileUserRole}>Role: {user.role}</span>
                </div>
              </div>
              <Button 
                type="secondary" 
                onClick={logout}
                className={styles.nav__mobileLogout}
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Nav; 